import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { chime } from '../lib/chime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentResult({ mode }) {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [sp] = useSearchParams();
  const sessionId = sp.get('session_id');
  const [status, setStatus] = useState('checking');
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (mode !== 'success' || !sessionId) return;
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      attempts += 1;
      setTries(attempts);
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        if (cancelled) return;
        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          // Auto-refresh auth so tier upgrade (Pro/Studio) unlocks premium UI instantly
          try { await refreshUser?.(); } catch { /* ignore */ }
          chime('success');
          return;
        }
        if (['failed', 'expired'].includes(res.data.payment_status)) {
          setStatus('failed');
          return;
        }
      } catch (e) {
        // keep polling briefly
      }
      if (attempts < 15 && !cancelled) {
        setTimeout(poll, 2000);
      } else {
        setStatus('timeout');
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [mode, sessionId]);

  if (mode === 'cancel') {
    return (
      <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
        <div className="max-w-md w-full text-center rounded-2xl border border-[#1E1E24] bg-[#0A0A0B] p-10">
          <XCircle className="w-16 h-16 text-zinc-500 mx-auto mb-6" />
          <h1 className="text-3xl font-black tracking-tight mb-3">Payment canceled</h1>
          <p className="text-zinc-400 mb-8">No worries — no charge was made. You can upgrade any time.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => navigate('/pricing')} className="bg-white text-black hover:bg-zinc-200 rounded-full" data-testid="pay-cancel-retry">Back to pricing</Button>
            <Button variant="outline" onClick={() => navigate('/dashboard')} className="border-white/15 rounded-full" data-testid="pay-cancel-dashboard">Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center p-6">
      <div className="max-w-md w-full text-center rounded-2xl border border-[#1E1E24] bg-[#0A0A0B] p-10">
        {status === 'paid' && (
          <>
            <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h1 className="text-3xl font-black tracking-tight mb-3">You're all set</h1>
            <p className="text-zinc-400 mb-8">Your subscription is active. Welcome to the next level.</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-white text-black hover:bg-zinc-200 rounded-full" data-testid="pay-success-cta">Go to Dashboard</Button>
          </>
        )}
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-[#06B6D4] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-black tracking-tight mb-3">Confirming payment…</h1>
            <p className="text-zinc-500 mb-4 text-sm">Attempt {tries} of 15</p>
            <p className="text-zinc-400">This usually takes a few seconds.</p>
          </>
        )}
        {status === 'failed' && (
          <>
            <XCircle className="w-16 h-16 text-rose-400 mx-auto mb-6" />
            <h1 className="text-3xl font-black tracking-tight mb-3">Payment failed</h1>
            <p className="text-zinc-400 mb-8">Please try again or use a different card.</p>
            <Button onClick={() => navigate('/pricing')} className="bg-white text-black hover:bg-zinc-200 rounded-full">Back to pricing</Button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <Loader2 className="w-16 h-16 text-zinc-500 mx-auto mb-6" />
            <h1 className="text-2xl font-black tracking-tight mb-3">Still processing…</h1>
            <p className="text-zinc-400 mb-8">Refresh in a moment or check your dashboard.</p>
            <Button onClick={() => navigate('/dashboard')} className="bg-white text-black hover:bg-zinc-200 rounded-full">Dashboard</Button>
          </>
        )}
      </div>
    </div>
  );
}
